import React, { useState } from 'react';
import { XMarkIcon, KeyIcon, ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';

import GraphqlService from '../../service/graphqlService';
import { useSnackbar } from '../../providers/SnackbarContext';
import useAnalytics from '../../hooks/useAnalytics';

const GPG_HEADER = '-----BEGIN PGP PUBLIC KEY BLOCK-----';
const GPG_FOOTER = '-----END PGP PUBLIC KEY BLOCK-----';

// Feature 032 (FR020 #13) - client-side structural validation of a GPG Armored public key.
// Mirrors the backend check so we can reject obviously malformed input before submitting.
const isValidGpgArmoredPublicKey = (value) => {
  if (!value || typeof value !== 'string') {
    return false;
  }
  const normalized = value.trim();
  if (!normalized.startsWith(GPG_HEADER) || !normalized.endsWith(GPG_FOOTER)) {
    return false;
  }
  const body = normalized.slice(GPG_HEADER.length, normalized.length - GPG_FOOTER.length).trim();
  return body.length > 0;
};

/**
 * GpgKeyModal
 * Feature 032 (FR020 #13) - Update the GPG public key for a logical user so future
 * credential rotations encrypt secrets with the new key (retires PROC081). After a
 * successful key update the operator can rotate every physical user linked to the
 * logical user so their credentials are re-encrypted with the new key (User Story 2).
 */
const GpgKeyModal = ({
  isOpen, onClose, onSuccess, credential, linkedCredentials = [], canRotate = false,
}) => {
  const openSnackBar = useSnackbar();
  const { setAnalytics } = useAnalytics();
  const [publicKey, setPublicKey] = useState('');
  const [validationError, setValidationError] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [keyUpdated, setKeyUpdated] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [rotationResults, setRotationResults] = useState(null);

  if (!isOpen) return null;

  const logicalUserName = credential?.logicalUser || '';
  const linkedIds = linkedCredentials.map((c) => c.id).filter(Boolean);

  const handleClose = () => {
    setPublicKey('');
    setValidationError('');
    setIsUpdating(false);
    setKeyUpdated(false);
    setIsRotating(false);
    setRotationResults(null);
    onClose();
  };

  const handleUpdateKey = async () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: 'certificate_management-provisioning-gpg_key-update' },
    });
    if (!isValidGpgArmoredPublicKey(publicKey)) {
      setValidationError('Enter a valid GPG Armored public key (starts with the BEGIN PGP PUBLIC KEY BLOCK header).');
      return;
    }
    setValidationError('');
    setIsUpdating(true);
    try {
      await GraphqlService.updateLogicalUserGpgKey(logicalUserName, publicKey.trim());
      setKeyUpdated(true);
      openSnackBar('GPG public key updated successfully.', 'success');
      if (typeof onSuccess === 'function') {
        onSuccess({ success: true, credential });
      }
    } catch (error) {
      console.error('Error updating GPG key:', error);
      openSnackBar(`Failed to update GPG key: ${error.message}`, 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRotateLinked = async () => {
    setAnalytics({
      event: 'button_clicked',
      extraParams: { name: 'certificate_management-provisioning-gpg_key-rotate' },
    });
    if (linkedIds.length === 0) {
      openSnackBar('No linked credentials to rotate.', 'info');
      return;
    }
    setIsRotating(true);
    try {
      const results = await GraphqlService.rotateLinkedCredentials(linkedIds);
      setRotationResults(results || []);
      const failures = (results || []).filter((r) => !r.success);
      if (failures.length === 0) {
        openSnackBar('All linked credentials rotated successfully.', 'success');
      } else {
        openSnackBar(`${failures.length} of ${results.length} rotations failed. See details below.`, 'warning');
      }
      if (typeof onSuccess === 'function') {
        onSuccess({ success: true, rotated: true, credential });
      }
    } catch (error) {
      console.error('Error rotating linked credentials:', error);
      openSnackBar(`Failed to rotate linked credentials: ${error.message}`, 'error');
    } finally {
      setIsRotating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <KeyIcon className="h-6 w-6 text-indigo-600" />
            <h2 className="text-xl font-bold text-gray-800">Update GPG Key</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            Update the GPG public key for logical user{' '}
            <span className="font-semibold text-gray-800">{logicalUserName || 'N/A'}</span>. Future credential
            rotations will encrypt the secret with this key.
          </p>

          {!keyUpdated ? (
            <>
              <label htmlFor="gpg-public-key" className="block text-sm font-medium text-gray-700">
                GPG Armored public key
              </label>
              <textarea
                id="gpg-public-key"
                value={publicKey}
                onChange={(e) => setPublicKey(e.target.value)}
                rows={10}
                placeholder={`${GPG_HEADER}\n...\n${GPG_FOOTER}`}
                className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-xs focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              {validationError && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <ExclamationTriangleIcon className="h-4 w-4" />
                  {validationError}
                </p>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateKey}
                  disabled={isUpdating || !publicKey.trim()}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUpdating ? 'Updating...' : 'Update Key'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-md p-3">
                <CheckCircleIcon className="h-5 w-5" />
                <span className="text-sm font-medium">GPG key updated for {logicalUserName}.</span>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <ArrowPathIcon className="h-4 w-4 text-indigo-600" />
                  Rotate linked credentials
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Rotate the {linkedIds.length} credential{linkedIds.length === 1 ? '' : 's'} linked to this logical
                  user so their new secrets are encrypted with the updated GPG key.
                </p>

                {rotationResults && (
                  <ul className="mt-3 space-y-1 max-h-48 overflow-y-auto">
                    {rotationResults.map((r) => (
                      <li key={r.physicalUserId} className="text-xs flex items-center gap-2">
                        {r.success ? (
                          <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                        ) : (
                          <ExclamationTriangleIcon className="h-4 w-4 text-red-600 flex-shrink-0" />
                        )}
                        <span className="font-mono text-gray-700 truncate">{r.physicalUserId}</span>
                        {!r.success && <span className="text-red-600">{r.message || 'Failed'}</span>}
                      </li>
                    ))}
                  </ul>
                )}

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Close
                  </button>
                  {canRotate && !rotationResults && (
                    <button
                      onClick={handleRotateLinked}
                      disabled={isRotating || linkedIds.length === 0}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isRotating ? 'Rotating...' : `Rotate ${linkedIds.length} credential${linkedIds.length === 1 ? '' : 's'}`}
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GpgKeyModal;
